<script lang="ts">
  import { afterNavigate } from '$app/navigation';
  import { page } from '$app/state';
  import { browser } from '$app/env';
  import { onMount } from 'svelte';

  let { children }: { children: import('svelte').Snippet } = $props();

  const siteUrl = 'https://dano.win/zpl';

  const orgSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ZPL Editor",
    "url": siteUrl,
    "description": "Free online ZPL label editor and designer. Visual drag-and-drop label builder with real-time ZPL preview, PNG export, and in-browser WebAssembly rendering.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free tier with 1 project; paid upgrades available"
    }
  });

  let user = $state<any>(null);

  /** Mark the current section in the nav (`/zpl/editor` lights up Home). */
  function isActive(href: string) {
    const path = page.url.pathname;
    if (href === '/zpl/') return path === '/zpl' || path === '/zpl/';
    return path === href || path.startsWith(`${href}/`);
  }

  async function checkAuth() {
    try {
      const response = await fetch('/zpl/api/auth/me');
      if (response.ok) {
        const data = await response.json();
        user = data;
      } else {
        user = null;
      }
    } catch (e) {
      user = null;
    }
  }

  async function logout() {
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch (e) {}
    window.location.href = '/zpl';
  }

  onMount(() => {
    checkAuth();
    if (browser) {
      // Precaching the app shell must not contend with first paint.
      const w = window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
      };
      const register = () => {
        import('virtual:pwa-register').then(({ registerSW }) => {
          registerSW({ immediate: true });
        });
      };
      const kick = () => {
        if (w.requestIdleCallback) w.requestIdleCallback(register, { timeout: 5000 });
        else setTimeout(register, 2500);
      };
      if (document.readyState === 'complete') kick();
      else window.addEventListener('load', kick, { once: true });
    }
  });
  afterNavigate(checkAuth);
</script>

<svelte:head>
  <!-- Organization Schema (JSON-LD) — injected globally -->
  {@html `<script type="application/ld+json">${orgSchema}</script>`}
</svelte:head>

<header class="site-header">
  <div class="header-inner">
    <a href="/zpl/" class="logo">
      <span class="logo-mark" aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="8" y1="9" x2="16" y2="9"></line>
          <line x1="8" y1="13" x2="16" y2="13"></line>
          <line x1="8" y1="17" x2="12" y2="17"></line>
        </svg>
      </span>
      <span class="logo-text">ZPL Editor</span>
    </a>
    <nav class="header-nav">
      <a
        href="/zpl/"
        class:active={isActive('/zpl/')}
        aria-current={isActive('/zpl/') ? 'page' : undefined}>Home</a
      >
      <a
        href="/zpl/editor"
        class:active={isActive('/zpl/editor')}
        aria-current={isActive('/zpl/editor') ? 'page' : undefined}>Editor</a
      >
      <a
        href="/zpl/samples"
        class:active={isActive('/zpl/samples')}
        aria-current={isActive('/zpl/samples') ? 'page' : undefined}>Samples</a
      >
      <a
        href="/zpl/pricing"
        class:active={isActive('/zpl/pricing')}
        aria-current={isActive('/zpl/pricing') ? 'page' : undefined}>Pricing</a
      >
      {#if user}
        <a href="/zpl/dashboard" class:active={isActive('/zpl/dashboard')}>Dashboard</a>
        {#if user.isAdmin}<a href="/zpl/admin" class:active={isActive('/zpl/admin')}>Admin</a>{/if}
        <a href="/zpl/account" class:active={isActive('/zpl/account')}>Account</a>
        <button onclick={logout} class="nav-btn">Logout</button>
      {:else}
        <a href="/auth/login?next=/zpl/dashboard" class="nav-cta">Sign in</a>
      {/if}
    </nav>
  </div>
</header>

<main class="main-content">
  {@render children()}
</main>

<footer class="site-footer">
  <div class="footer-inner">
    <nav>
      <a href="/zpl/">Home</a>
      <a href="/zpl/editor">Editor</a>
      <a href="/zpl/samples">Samples</a>
      <a href="/zpl/pricing">Pricing</a>
      <a href="/zpl/about">About</a>
      <a href="/zpl/privacy">Privacy Policy</a>
    </nav>
    <p class="copyright">© 2026 ZPL Editor. Not affiliated with Zebra Technologies.</p>
  </div>
</footer>

<style>
  .site-header {
    background: rgba(255, 255, 255, 0.86);
    backdrop-filter: saturate(180%) blur(12px);
    -webkit-backdrop-filter: saturate(180%) blur(12px);
    border-bottom: 1px solid var(--border);
    padding: 0;
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .header-inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 0.65rem 1.5rem;
    display: flex;
    align-items: center;
    gap: 1.25rem;
  }

  .logo {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 1.02rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--text);
    text-decoration: none;
    flex-shrink: 0;
  }

  .logo:hover {
    text-decoration: none;
    color: var(--primary-dark);
  }

  .logo-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 7px;
    background: linear-gradient(135deg, var(--primary), #7c3aed);
    color: #fff;
    box-shadow: 0 2px 6px rgba(79, 70, 229, 0.35);
  }

  .logo-text {
    white-space: nowrap;
  }

  .header-nav {
    display: flex;
    gap: 0.15rem;
    margin-left: auto;
    align-items: center;
  }

  .header-nav a {
    color: var(--text-secondary);
    font-size: 0.85rem;
    font-weight: 500;
    text-decoration: none;
    padding: 0.4rem 0.65rem;
    border-radius: 7px;
    transition: background 0.15s, color 0.15s;
  }

  .header-nav a:hover {
    background: var(--primary-subtle);
    color: var(--primary-dark);
    text-decoration: none;
  }

  .header-nav a.active {
    background: var(--primary-subtle);
    color: var(--primary-dark);
    font-weight: 600;
  }

  .nav-cta {
    background: var(--primary) !important;
    color: #fff !important;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(79, 70, 229, 0.35);
  }

  .nav-cta:hover {
    background: var(--primary-dark) !important;
    color: #fff !important;
  }

  .logout-form {
    display: inline;
  }

  .nav-btn {
    background: none;
    border: 1px solid transparent;
    color: var(--text-secondary);
    font-size: 0.85rem;
    font-weight: 500;
    padding: 0.35rem 0.65rem;
    border-radius: 7px;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s, color 0.15s;
  }

  .nav-btn:hover {
    background: var(--primary-subtle);
    color: var(--primary-dark);
  }

  .main-content {
    padding: 2rem 1.5rem;
    min-height: calc(100vh - 140px);
    max-width: 1180px;
    margin: 0 auto;
  }

  /* Editor route uses full-bleed workspace — editor-shell uses display:contents */
  :global(.main-content:has(.editor)) {
    padding: 0;
    min-height: 0;
    max-width: none;
    overflow: hidden;
  }

  .site-footer {
    background: #0f172a;
    padding: 0;
  }

  .footer-inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 1.4rem 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .site-footer nav {
    display: flex;
    gap: 1.25rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .site-footer a {
    color: #94a3b8;
    font-size: 0.82rem;
    text-decoration: none;
    transition: color 0.15s;
  }

  .site-footer a:hover {
    color: #fff;
    text-decoration: none;
  }

  .copyright {
    margin: 0;
    font-size: 0.78rem;
    color: #94a3b8;
  }

  @media (max-width: 720px) {
    .header-inner {
      flex-wrap: wrap;
      gap: 0.5rem;
      padding: 0.6rem 1rem;
    }

    .header-nav {
      flex-wrap: wrap;
      margin-left: 0;
      width: 100%;
      overflow-x: auto;
      padding-bottom: 0.15rem;
    }

    .header-nav a,
    .nav-btn {
      white-space: nowrap;
    }

    .footer-inner {
      flex-direction: column;
      text-align: center;
    }
  }
</style>
